import { wei } from '@kwenta/wei'
import { useCallback } from 'react'

import { setOpenModal } from 'state/app/reducer'
import {
	handleOneClickOperation,
	refetchTradePreview,
	submitIsolatedMarginOrder,
} from 'state/futures/actions'
import {
	selectMarketAsset,
	selectMarketInfo,
	selectPerpsProvider,
	selectTradeOrderType,
	selectTradePanelInputs,
	selectTradingMode,
} from 'state/futures/common/selectors'
import { approveIsolatedMargin } from 'state/futures/isolatedMargin/actions'
import {
	selectIsolatedMarginAllowanceValid,
	selectPosition,
	selectTradePreview,
} from 'state/futures/selectors'
import { TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector, usePollAction } from 'state/hooks'

import TradeConfirmationModal from './TradeConfirmationModal'

export default function TradeConfirmationModalSmartMargin() {
	const dispatch = useAppDispatch()

	const marketInfo = useAppSelector(selectMarketInfo)
	const tradePreview = useAppSelector(selectTradePreview)
	const orderType = useAppSelector(selectTradeOrderType)
	const marketAsset = useAppSelector(selectMarketAsset)
	const { stopLossPrice, takeProfitPrice } = useAppSelector(selectTradePanelInputs)
	const allowanceValid = useAppSelector(selectIsolatedMarginAllowanceValid)
	const tradingMode = useAppSelector(selectTradingMode)
	const provider = useAppSelector(selectPerpsProvider)
	const position = useAppSelector(selectPosition)

	usePollAction('refresh_preview', refetchTradePreview, { intervalTime: 10000 })

	const onDismiss = useCallback(() => {
		dispatch(setOpenModal(null))
	}, [dispatch])

	const handleApproveSmartMargin = useCallback(async () => {
		dispatch(approveIsolatedMargin())
	}, [dispatch])

	const onSubmit = useCallback(() => {
		dispatch(submitIsolatedMarginOrder({ overridePriceProtection: true, provider }))
	}, [dispatch, provider])

	const handleSubmit = useCallback(async () => {
		tradingMode === TradingModes.ONE_CLICK
			? dispatch(handleOneClickOperation(onSubmit))
			: onSubmit()
	}, [dispatch, tradingMode, onSubmit])

	return (
		<TradeConfirmationModal
			provider={provider}
			onDismiss={onDismiss}
			orderType={orderType}
			marketAsset={marketAsset}
			onApproveAllowance={handleApproveSmartMargin}
			sltp={{
				stopLossPrice: stopLossPrice,
				takeProfitPrice: takeProfitPrice,
			}}
			tradePreview={{
				margin: tradePreview?.margin,
				priceImpact: tradePreview?.priceImpact ? wei(tradePreview.priceImpact) : undefined,
				size: wei(tradePreview?.newSize ?? 0),
				price: wei(tradePreview?.fillPrice ?? 0),
				exceedsPriceProtection: tradePreview?.exceedsPriceProtection,
				sizeDelta: wei(tradePreview?.sizeDelta ?? 0),
				liqPrice: wei(tradePreview?.liqPrice ?? 0),
			}}
			positionDetails={position?.details}
			onSubmitOrder={handleSubmit}
			allowanceValid={allowanceValid}
			fees={{
				tradeFee: wei(tradePreview?.fee ?? 0),
				executionFee: wei(marketInfo?.settlementFee ?? 0),
			}}
		/>
	)
}
